import Image from 'next/image'
import { FunctionComponent } from 'react'
import { useMount } from 'react-use'
import styled from 'styled-components'
import avatar1 from '@/assets/images/insight-report/avatar1.svg'
import avatar2 from '@/assets/images/insight-report/avatar2.svg'
import avatar3 from '@/assets/images/insight-report/avatar3.svg'
import avatar4 from '@/assets/images/insight-report/avatar4.svg'
import avatar5 from '@/assets/images/insight-report/avatar5.svg'
import type { OurIntlShape } from '@/i18n/typing'
import PlatformImage from '@/utils/export-pptx/components/platform-image'
import {
  Card,
  CardTitle,
  SlideContainer,
  SlideTitle,
} from '@/utils/export-pptx/components/slides/styles'
import { PptxObjectType } from '@/utils/export-pptx/constants'
import sliceTextByDisplayLength from '@/utils/export-pptx/slice-text-by-display-length'
import type { CommentBoardSlide } from '@/utils/export-pptx/types/slide'

const AVATARS = [avatar1, avatar2, avatar3, avatar4, avatar5] as const
const AVATAR_AMOUNT = AVATARS.length

interface SlideProps {
  slide: CommentBoardSlide
  onSlideReady?: (slide: CommentBoardSlide) => void
  formatMessage: OurIntlShape['formatMessage']
}

const SlideComponent: FunctionComponent<SlideProps> = ({
  slide,
  onSlideReady,
  formatMessage,
}) => {
  useMount(() => {
    onSlideReady?.(slide)
  })

  return (
    <SlideContainer data-pptx-object={PptxObjectType.Shape}>
      <SlideTitle data-pptx-object={PptxObjectType.Shape}>
        <PlatformImage
          height={54}
          platform={slide.payload.platform}
          width={54}
        />
        <span data-pptx-object={PptxObjectType.Text}>
          {formatMessage({
            id: 'autoreport:analysis_section_comment',
          })}
        </span>
      </SlideTitle>

      <Card data-pptx-object={PptxObjectType.Shape}>
        <CardTitle data-pptx-object={PptxObjectType.Text}>
          {slide.payload.title}
        </CardTitle>

        <CardContent>
          {slide.payload.commentList.map((item, index) => {
            const avatarIndex = index % AVATAR_AMOUNT

            return (
              <CommentCard key={item.id}>
                <Image
                  priority
                  unoptimized
                  alt='kol thumbnail'
                  data-pptx-object={PptxObjectType.Image}
                  height={40}
                  src={AVATARS[avatarIndex]}
                  style={{ borderRadius: '50%' }}
                  width={40}
                />

                <div>
                  <CommentUsername data-pptx-object={PptxObjectType.Text}>
                    {item.name}
                  </CommentUsername>
                  <CommentText data-pptx-object={PptxObjectType.Text}>
                    {sliceTextByDisplayLength(item.comment, 80)}
                  </CommentText>

                  <CommentDate data-pptx-object={PptxObjectType.Text}>
                    {item.date} | {formatMessage({ id: 'general:post_from' })}
                    {item.kol.name}
                  </CommentDate>
                </div>

                <CommentCardBorderBottom
                  data-pptx-object={PptxObjectType.Shape}
                />
              </CommentCard>
            )
          })}
        </CardContent>
      </Card>
    </SlideContainer>
  )
}

const CardContent = styled.div`
  display: flex;
  flex-wrap: wrap;

  gap: 0 24px;

  column-gap: 24px;

  width: 100%;
  max-width: 100%;
`

const CommentCard = styled.div`
  position: relative;

  display: flex;
  gap: 8px;

  padding: 16px 12px;
  width: calc(33% - 24px);
`

const CommentCardBorderBottom = styled.div`
  position: absolute;

  bottom: 0;
  left: 0;

  width: 100%;
  height: 1px;
  background-color: ${({ theme }): string => theme.colors.border.divider};

  ${CommentCard}:nth-child(4n) & {
    display: none;
  }
`

const CommentUsername = styled.div`
  margin-bottom: 4px;

  font-size: 14px;
  font-weight: 700;
  line-height: 140%;
  color: ${({ theme }): string => theme.colors.text.primary};
`

const CommentText = styled.div`
  margin-bottom: 12px;

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;

  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }): string => theme.colors.text.secondary};
`

const CommentDate = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: ${({ theme }): string => theme.colors.text.tooltip};
`

export default SlideComponent
